import JSZip from 'jszip';
import {
    DicomBase,
    GenerationModelDetails,
    ObjectStatus,
} from '~/domain/dicom/dicomBase';
import { DicomProcessor } from '~/domain/services/dicomProcessor';
import { handleError } from '~/utils/errors';
import { decompressGZip } from './gzip';

export function requestFileInput(
    onFileLoaded: (data: ArrayBuffer) => unknown,
    multiple: boolean
) {
    const input = document.createElement('input');
    input.type = 'file';
    input.multiple = multiple;
    input.onchange = function () {
        const files = input.files;
        if (files) {
            for (let i = 0; i < files.length; ++i) {
                const file = files.item(i);
                if (file) {
                    const reader = new FileReader();
                    reader.onload = () => {
                        if (reader.result) {
                            onFileLoaded(reader.result as ArrayBuffer);
                        }
                    };
                    reader.onerror = () => {
                        throw new Error(`FileHandler.requestFileInput faced an error reading file ${file.name}:\
                        ${reader.error}`);
                    };
                    reader.readAsArrayBuffer(file);
                }
            }
        }
    };
    input.click();
    input.remove();
}

/**
 * Function that will get a complete filename as a parameter and divide it into fileName and extension.\
 * The filename is divided by the last dot. And the dot won't be included in either the fileName nor the extension.\
 * input of "dicom.dcm.gz" will return {fileName: "dicom.dcm", fileExtension: "gz"}\
 * input of "dicom.dcm" will return {fileName: "dicom", fileExtension: "dcm"}
 * @param completeName The filename with it's file extension. Format [filename].[extension]
 * @returns Type with both the fileName and fileExtension.\
 * If the dot is missing or it is the first or last character in the filename, fileExtension is undefined.
 */
function extractFilenameAndExtension(completeName: string): {
    fileName: string;
    fileExtension?: string;
} {
    const lastDot = completeName.lastIndexOf('.');
    if (lastDot > 0 && lastDot < completeName.length - 1) {
        return {
            fileName: completeName.substring(0, lastDot).toLowerCase(),
            fileExtension: completeName.substring(lastDot + 1).toLowerCase(),
        };
    } else {
        return { fileName: completeName };
    }
}

/**
 * Function to process a {@link File}.\
 * It's extension is extracted to know what kind of data the {@link File} should contain.
 * File's contents are converted into {@link ArrayBuffer} asynchronously.\
 * Once the conversion has been completed, the contents are given to {@link processFileContent} function for processing.
 *
 * If the file extension is .zip it first decompresses it and then process files inside.
 *
 * @param file {@link File} the contents of which are to be processed.
 */
export async function processFile(
    file: File,
    dicomProcessor: DicomProcessor,
    objectStatus?: ObjectStatus,
    model?: GenerationModelDetails
): Promise<DicomBase | null> {
    const { fileName, fileExtension } = extractFilenameAndExtension(file.name);
    if (fileExtension === 'zip') {
        const zip = new JSZip();
        const unzipped = await zip.loadAsync(file);
        const files = Object.keys(unzipped.files);
        await Promise.all(
            files.map(async (fileName) => {
                const blob = await unzipped.files[fileName].async('blob');
                return await processFile(
                    new File([blob], fileName),
                    dicomProcessor,
                    objectStatus,
                    model
                );
            })
        );
        return null;
    } else {
        try {
            const fileArrayBuffer = await file.arrayBuffer();
            const dicomData = processFileContent(
                dicomProcessor,
                fileArrayBuffer,
                fileName,
                fileExtension
            );
            if (dicomData && objectStatus) {
                dicomData.objectStatus = objectStatus;
            }
            if (dicomData && model) {
                dicomData.generationModel = model;
            }
            return dicomData;
        } catch (error) {
            handleError(
                error,
                `Error processing file '${file.name}': ${error}`,
                'processFileError'
            );
            console.error(error);
            return null;
        }
    }
}

/**
 * Function that will process {@link ArrayBuffer} contents of different types of {@link File Files}.\
 * {@link fileExtension} is used to determine the type of data. And the correct processing functionality.
 * @param arrayBuffer Contents of a {@link File} that is to be processed.
 * @param fileName The filename without it's last extension.
 * @param fileExtension Filename's last extension.
 * @returns True if the file contents could successfully be processed, false otherwise.
 */
export function processFileContent(
    dicomProcessor: DicomProcessor,
    arrayBuffer: ArrayBuffer,
    fileName: string,
    fileExtension?: string
): DicomBase | null {
    switch (fileExtension) {
        case 'gz': {
            const nameAndExtension = extractFilenameAndExtension(fileName);
            return processFileContent(
                dicomProcessor,
                decompressGZip(arrayBuffer),
                nameAndExtension.fileName,
                nameAndExtension.fileExtension
            );
        }
        case 'dcm': {
            const dicomData = dicomProcessor.processDicomData(arrayBuffer);
            if (!dicomData) {
                console.error(
                    `DICOM parsing failed for ${fileName}.${fileExtension}`
                );
            }
            return dicomData;
        }
        default:
            console.error(
                `Attempt to process contents of file with unsupported file-extension ${fileName}.${fileExtension}`
            );
            return null;
    }
}
